import React from 'react';
import { ChevronRight } from 'lucide-react';

const AIOnboardingEntry = ({ onSelectPath }) => {
  const paths = [
    {
      id: 'university',
      title: 'I already have a university in mind',
      description: 'Get personalized guidance and compare your choice',
      icon: '🎓',
    },
    {
      id: 'field',
      title: 'I know the field, not the university',
      description: 'Find universities that match your interests',
      icon: '🔍',
    },
    {
      id: 'unsure',
      title: "I'm not sure yet",
      description: 'Let AI help you explore and discover',
      icon: '✨',
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-black to-gray-950 flex flex-col items-center justify-center px-4 py-12">
      {/* Animated background elements */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-72 h-72 bg-primary/20 rounded-full blur-3xl opacity-30"></div>
        <div className="absolute bottom-20 right-10 w-72 h-72 bg-primary/10 rounded-full blur-3xl opacity-25"></div>
      </div>

      <div className="relative z-10 w-full max-w-6xl">
        {/* Header Section */}
        <div className="text-center mb-16">
          <h1 className="text-5xl md:text-6xl font-bold text-white mb-4 tracking-tight">
            Find your <span className="bg-gradient-to-r from-primary to-primary bg-clip-text text-transparent">university path</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-400 max-w-2xl mx-auto leading-relaxed">
            UniSteps uses AI to help you discover and choose the perfect university. 
            Answer a few questions and get personalized recommendations tailored to your goals.
          </p>
        </div>

        {/* Cards Grid */}
        <div className="grid md:grid-cols-3 gap-6 md:gap-8">
          {paths.map((path, index) => (
            <button
              key={path.id}
              onClick={() => onSelectPath(path.id)}
              className="group relative h-96 overflow-hidden rounded-2xl bg-gradient-to-br from-gray-900 to-black border border-gray-700 hover:border-primary/60 transition-all duration-300 cursor-pointer"
              style={{
                animation: `slideInUp 0.6s ease-out ${index * 0.1}s backwards`,
              }}
            >
              {/* Hover gradient background */}
              <div className="absolute inset-0 bg-gradient-to-br from-primary/0 to-primary/0 group-hover:from-primary/10 group-hover:to-primary/5 transition-all duration-300"></div>

              {/* Gold accent line on hover */}
              <div className="absolute top-0 left-0 w-0 h-1 bg-gradient-to-r from-amber-400 to-amber-600 group-hover:w-full transition-all duration-500"></div>

              {/* Content */}
              <div className="relative p-8 h-full flex flex-col justify-between">
                {/* Icon */}
                <div className="text-6xl mb-4 transform group-hover:scale-110 transition-transform duration-300">
                  {path.icon}
                </div>

                {/* Text */}
                <div className="flex-1 flex flex-col justify-end">
                  <h3 className="text-xl font-semibold text-white mb-3 text-left group-hover:text-primary transition-colors duration-300">
                    {path.title}
                  </h3>
                  <p className="text-gray-400 text-sm leading-relaxed mb-4 text-left">
                    {path.description}
                  </p>

                  {/* Arrow indicator */}
                  <div className="flex items-center text-primary opacity-0 group-hover:opacity-100 transform group-hover:translate-x-2 transition-all duration-300">
                    <span className="text-sm font-medium mr-2">Get started</span>
                    <ChevronRight size={18} />
                  </div>
                </div>
              </div>

              {/* Corner accent on hover */}
              <div className="absolute bottom-0 right-0 w-20 h-20 bg-gradient-to-tl from-primary/10 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 rounded-tl-full"></div>
            </button>
          ))}
        </div>

        {/* Footer text */}
        <div className="text-center mt-16 text-gray-500 text-sm">
          <p>✓ No credit card required • ✓ Takes 2-3 minutes • ✓ Personalized recommendations</p>
        </div>
      </div>

      {/* CSS Animations */}
      <style>{`
        @keyframes slideInUp {
          from {
            opacity: 0;
            transform: translateY(30px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }

        @keyframes fadeIn {
          from {
            opacity: 0;
          }
          to {
            opacity: 1;
          }
        }
      `}</style>
    </div>
  );
};

export default AIOnboardingEntry;
